import React, { useEffect, useRef, useState } from 'react'
import { mountThreeScene, BRAND } from '@lib/threeScene'
import PrimitivesFallback from './PrimitivesFallback'

/**
 * Compositions of cube, cone and sphere in three tones taken from the logo —
 * gold face, cream specular, and its mid-grey bevel standing in for the
 * charcoal body, which is invisible against this site's black page.
 *
 * Each `variant` is a different arrangement so the same three primitives can
 * carry several sections without repeating. Materials stay metallic and the
 * palette stays closed: three colours, no others.
 */

/* Material notes.
 *
 * Metalness is kept in the 0.55-0.75 band rather than pushed to 1. A fully
 * metallic surface has essentially no diffuse response, so on a dark page it
 * only shows what the environment gives it — which is why the first pass
 * rendered a brown sphere instead of a gold one. Some diffuse keeps the base
 * colour readable; the environment map supplies the sheen on top.
 *
 * Roughness is also raised. A near-mirror finish concentrates the highlight
 * into a pinpoint and leaves the rest of the form dark; a softer finish
 * spreads it across the surface so the shape reads.
 */
const LAYOUTS = {
  // Balanced trio, gently orbiting. General-purpose section mark.
  trio: [
    { shape: 'cube', pos: [-1.5, 0.1, 0], size: 1.25, color: BRAND.goldBright, metal: 0.72, rough: 0.3, spin: [0.25, 0.4, 0] },
    { shape: 'cone', pos: [1.45, 0.05, -0.4], size: 0.95, color: BRAND.cream, metal: 0.45, rough: 0.38, spin: [0, 0.5, 0] },
    { shape: 'sphere', pos: [0.15, -0.75, 0.9], size: 0.78, color: BRAND.pewter, metal: 0.68, rough: 0.32, spin: [0.2, 0.3, 0] }
  ],
  // Vertical, stacked — reads as sequence or process.
  column: [
    { shape: 'sphere', pos: [0, 1.55, 0], size: 0.72, color: BRAND.goldBright, metal: 0.7, rough: 0.28, spin: [0.2, 0.35, 0] },
    { shape: 'cone', pos: [0, 0.15, 0], size: 1.05, color: BRAND.cream, metal: 0.42, rough: 0.4, spin: [0, 0.42, 0] },
    { shape: 'cube', pos: [0, -1.45, 0], size: 1.35, color: BRAND.pewter, metal: 0.65, rough: 0.34, spin: [0.15, 0.3, 0.05] }
  ],
  // Wide and shallow, for narrow banner slots.
  row: [
    { shape: 'cone', pos: [-2.1, 0, 0], size: 0.9, color: BRAND.goldBright, metal: 0.72, rough: 0.3, spin: [0, 0.45, 0] },
    { shape: 'sphere', pos: [0, 0.1, 0], size: 0.85, color: BRAND.cream, metal: 0.45, rough: 0.36, spin: [0.18, 0.28, 0] },
    { shape: 'cube', pos: [2.1, -0.05, 0], size: 1.15, color: BRAND.pewter, metal: 0.66, rough: 0.32, spin: [0.2, 0.35, 0.05] }
  ],
  // Off-axis cluster, more editorial than diagrammatic.
  cluster: [
    { shape: 'cube', pos: [-0.95, 0.6, -0.5], size: 1.5, color: BRAND.pewter, metal: 0.64, rough: 0.34, spin: [0.12, 0.26, 0.04] },
    { shape: 'sphere', pos: [1.15, 0.35, 0.4], size: 0.92, color: BRAND.goldBright, metal: 0.74, rough: 0.26, spin: [0.22, 0.34, 0] },
    { shape: 'cone', pos: [0.05, -1.15, 0.75], size: 1.0, color: BRAND.cream, metal: 0.44, rough: 0.38, spin: [0, 0.48, 0] }
  ]
}

function makeGeometry(THREE, shape, size) {
  if (shape === 'cube') return new THREE.BoxGeometry(size, size, size)
  if (shape === 'cone') return new THREE.ConeGeometry(size * 0.62, size * 1.35, 48)
  return new THREE.SphereGeometry(size * 0.62, 48, 32)
}

export default function BrandPrimitives({ variant = 'trio', className = '', label }) {
  const hostRef = useRef(null)
  const [active, setActive] = useState(false)

  useEffect(() => {
    const host = hostRef.current
    if (!host) return

    const layout = LAYOUTS[variant] || LAYOUTS.trio

    return mountThreeScene(
      host,
      ({ THREE, scene, camera }) => {
        camera.position.set(0, 0.6, 7.2)
        camera.lookAt(0, 0, 0)

        scene.add(new THREE.AmbientLight(0xffffff, 0.55))

        // Warm key from upper right is what makes the gold read as metal.
        const key = new THREE.DirectionalLight(0xfff2cc, 3.1)
        key.position.set(4.5, 6.5, 5)
        key.castShadow = true
        key.shadow.mapSize.set(1024, 1024)
        key.shadow.camera.near = 1
        key.shadow.camera.far = 30
        key.shadow.bias = -0.0015
        scene.add(key)

        // Cool rim separates the silhouettes from the black page.
        const rim = new THREE.DirectionalLight(0xbfd8ff, 1.6)
        rim.position.set(-5.5, 2.5, -4.5)
        scene.add(rim)

        const fill = new THREE.PointLight(BRAND.goldBright, 1.9, 26)
        fill.position.set(-2.5, -1.5, 3.5)
        scene.add(fill)

        const group = new THREE.Group()
        const created = []
        const meshes = []

        layout.forEach((item) => {
          const geo = makeGeometry(THREE, item.shape, item.size)
          const mat = new THREE.MeshStandardMaterial({
            color: item.color,
            metalness: item.metal,
            roughness: item.rough,
            // Faint self-illumination keeps every form separated from a pure
            // black page even where no light reaches it.
            emissive: 0x121212,
            emissiveIntensity: 0.6
          })
          const mesh = new THREE.Mesh(geo, mat)
          mesh.position.set(...item.pos)
          mesh.castShadow = true
          mesh.receiveShadow = true
          group.add(mesh)
          meshes.push({ mesh, spin: item.spin, baseY: item.pos[1] })
          created.push({ geo, mat })
        })

        scene.add(group)

        return {
          update: (t, px = 0, py = 0) => {
            group.rotation.y = t * 0.55 + px * 0.22
            group.rotation.x = Math.sin(t * 0.5) * 0.06 + py * 0.1
            meshes.forEach((m, i) => {
              m.mesh.rotation.x += m.spin[0] * 0.004
              m.mesh.rotation.y += m.spin[1] * 0.004
              m.mesh.rotation.z += m.spin[2] * 0.004
              // Slight counter-phase drift so they don't move as one block.
              m.mesh.position.y = m.baseY + Math.sin(t * 1.2 + i * 1.9) * 0.11
            })
          },
          dispose: () => created.forEach(({ geo, mat }) => { geo.dispose(); mat.dispose() })
        }
      },
      { onReady: () => setActive(true) }
    )
  }, [variant])

  return (
    <div className={`relative ${className}`}>
      {/* Hidden with a class, not a transition: the renderer is
          alpha-transparent so a fallback left in place shows through, and a
          transition never completes in a backgrounded tab. */}
      <div className={active ? 'hidden' : ''} aria-hidden={active ? 'true' : undefined}>
        <PrimitivesFallback variant={variant} className="w-full h-auto" />
      </div>

      <div
        ref={hostRef}
        className={`absolute inset-0 transition-opacity duration-700 ${active ? 'opacity-100' : 'opacity-0'}`}
        role="img"
        aria-label={label || 'Rotating arrangement of a cube, a cone and a sphere in the brand palette.'}
      />
    </div>
  )
}
